import type { FC } from 'react';
import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { Button, Text } from '../../../ui-components';
import { AnswerField } from './AnswerField';
import type { AnswerTypes, ReplyTypes } from '../../../bff-api/turn-to/model';

export interface AnswerReplyItemProps {
    item: AnswerTypes | ReplyTypes;
    noPadding?: boolean;
    type?: 'bestAnswer' | 'regular';
}

export const AnswerReplyItem: FC<AnswerReplyItemProps> = ({
    noPadding = false,
    item,
    type = 'regular',
}) => {
    const {
        isOpen: isReplyTextfieldVisible,
        onOpen: onReplyTextfieldVisible,
        onClose: onHideReplyTextfield,
    } = useDisclosure();

    return (
        <Box
            key={item.id}
            padding={noPadding ? 0 : 4}
            borderTop={type === 'bestAnswer' ? '0px' : '1px'}
            borderColor="slt-pepper.400"
            marginTop={2}
        >
            <Flex className="space-x-1" marginBottom={2}>
                {type === 'bestAnswer' && (
                    <Text style="bold" trim>
                        <FormattedMessage defaultMessage="Best Answer:" id="turn-to.best-answer" />
                    </Text>
                )}
                <Text>{item.text}</Text>
            </Flex>
            <Flex
                marginTop={1}
                flexWrap={type === 'bestAnswer' ? 'wrap' : 'nowrap'}
                className={type === 'bestAnswer' ? 'space-y-2' : 'space-x-2'}
            >
                <Box width={type === 'bestAnswer' ? '100%' : 'auto'}>
                    <Text size="small">
                        {item.user?.firstName
                            ? `${item.user?.firstName} ${item.user?.lastName?.[0] ?? ''}`
                            : (item.user?.nickName ?? '')}
                    </Text>
                </Box>

                <Flex className="space-x-2">
                    <Box>
                        <Text size="small" color="slt-pepper.700">
                            {new Intl.DateTimeFormat('default', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                            }).format(new Date(item?.dateCreated))}
                        </Text>
                    </Box>
                    <Button variant="text-button" onClick={onReplyTextfieldVisible}>
                        <Text size="small">
                            <FormattedMessage defaultMessage="Reply" id="turn-to.reply" />
                        </Text>
                    </Button>
                    <Button variant="text-button">
                        <Text size="small">
                            <FormattedMessage defaultMessage="Inaccurate" id="turn-to.inaccurate" />
                        </Text>
                    </Button>
                </Flex>
            </Flex>
            {isReplyTextfieldVisible && (
                <AnswerField
                    id={item.id}
                    onClose={onHideReplyTextfield}
                    type="reply"
                    onCloseField={onHideReplyTextfield}
                />
            )}

            {type !== 'bestAnswer' &&
                item.replies.map((reply) => (
                    <Box key={reply.id} paddingLeft={4} marginTop={4}>
                        <AnswerReplyItem item={reply} />
                    </Box>
                ))}
        </Box>
    );
};
